import { useQuery } from '@tanstack/react-query';
import { LearnableDetailsApi, UserDictionaryApi } from 'src/api';
import { API_TARGET_ITEM_TYPES } from 'src/constants/itemTypes';
import { getLanguagePairId } from 'src/utils/language/getLanguagePairId';

/** How many recently-learnt words to check before giving up on finding footage. */
const MAX_CANDIDATES = 5;

export type LocalPronunciationClip = {
  src: string;
  /** The word itself, shown as the caption over the clip. */
  phrase: string;
};

/**
 * Finds a word the learner has recently learnt that has Learn with Locals footage
 * attached, so the upsell can show a local actually saying a word they know.
 *
 * Not every learnable has video, so this walks the recent words in order and takes
 * the first one that does. When none of them do, it resolves to null and the screen
 * renders without a clip.
 */
export const useLocalPronunciationClip = (): LocalPronunciationClip | null => {
  const languagePairId = getLanguagePairId();

  const { data: recentLearnables } = useQuery({
    queryKey: ['pronunciation_upsell_recent_learnables', languagePairId],
    queryFn: () =>
      UserDictionaryApi.getRandomKnownLearnables(languagePairId as number, {
        typesToExclude: [API_TARGET_ITEM_TYPES.ALPHABET],
        forPronunciation: true,
      }),
    enabled: !!languagePairId,
  });

  const learnableIds = recentLearnables?.learnables.map(({ id }) => id).slice(0, MAX_CANDIDATES);

  const { data: clip } = useQuery<LocalPronunciationClip | null>({
    queryKey: ['pronunciation_upsell_clip', languagePairId, learnableIds],
    queryFn: async () => {
      const details = await Promise.all(
        (learnableIds as number[]).map(id =>
          LearnableDetailsApi.getLearnableDetails(id).catch(() => null),
        ),
      );

      const withVideo = details.find(detail => !!detail?.videoUrls.length);
      if (!withVideo) return null;

      return { src: withVideo.videoUrls[0], phrase: withVideo.targetValue };
    },
    enabled: !!learnableIds?.length,
  });

  return clip ?? null;
};
